import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link, type Location } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login } from '../../auth/authSlice';
import { LanguageSelector } from '../../components/LanguageSelector';
import { isHostIpOrLoopback, deduceWebIdFromHost } from '../../utils/hostUtils';

export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { status, error, requiresWebId } = useAppSelector((state) => state.auth);

  // Hostname stabile per la sessione: lo leggo a init, no state.
  // - host testuale (es. `crm.acme.com`): deduco il webId dal primo segmento
  //   e tengo il campo Istanza nascosto.
  // - host IP / loopback: la deduzione è impossibile, mostro il campo.
  // VITE_DEFAULT_WEB_ID (env) ha la priorità se valorizzato (override esplicito).
  const host = window.location.hostname;
  const isIpHost = isHostIpOrLoopback(host);
  const deducedWebId = deduceWebIdFromHost(host);
  // requiresWebId arriva da Redux dopo un 412 (vedi authSlice). Fa rendere
  // visibile il campo Istanza anche su host testuale così l'utente può
  // correggere la deduzione.
  const showWebIdField = isIpHost || requiresWebId;

  // Priorità del valore iniziale del campo webId:
  // - Host testuale → vince il valore DEDOTTO dall'URL (ignoriamo VITE_DEFAULT_WEB_ID
  //   perché altrimenti l'env override impedirebbe la deduzione di funzionare).
  // - Host IP/loopback → il campo è visibile, l'utente lo digita. Mostriamo
  //   VITE_DEFAULT_WEB_ID come prefill ergonomico, fallback stringa vuota.
  const [webId, setWebId] = useState(
    isIpHost
      ? (import.meta.env.VITE_DEFAULT_WEB_ID || '')
      : (deducedWebId || '')
  );
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');

  // Destinazione post-login: la location intera salvata da AuthGuard, non il
  // solo pathname. `search` e `hash` fanno parte dell'indirizzo che l'utente
  // stava cercando di raggiungere — perderli significa atterrare su una pagina
  // che sembra quella giusta ma ha perso il contesto (es. i form aperti con
  // ?personId=/?businessId=/?messageId= si aprirebbero vuoti).
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`
    : '/tasks';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const result = await dispatch(login({ webId, emailAddress, password }));
    if (login.fulfilled.match(result)) {
      navigate(from, { replace: true });
    }
  }

  return (
    <div className="login-page">
      <div className="login-brand">
        <img src="/logo.svg" alt={t('brand.name')}
             className="logo" width="100"/>
        <h1>
          <span className="login-brand__accent">D'vel</span> CRM
        </h1>
        <p className="login-brand__tagline">{t('brand.tagline')}</p>
      </div>
      <LanguageSelector />
      <form onSubmit={handleSubmit} className="login-form">
        <h2 className="text-center text-lg mb-xl">{t('auth.login')}</h2>

        {error && !requiresWebId && (
          // Quando requiresWebId=true (= 412 dal backend) sopprimiamo il
          // messaggio generico grezzo (es. "No Company exists with the key
          // {webId=testdev}"). Il solo messaggio mostrato è quello i18n
          // contestuale renderizzato vicino al campo Istanza più sotto.
          <div className="text-error text-center mb-lg">
            {error}
          </div>
        )}

        {requiresWebId && (
          <div className="text-error text-center mb-md">
            {t('auth.errorWebIdRequired')}
          </div>
        )}
        {showWebIdField && (
          <TextField label={t('auth.instance')} value={webId} onChange={(e) => setWebId(e.target.value)} required
            fullWidth size="small" className="form-field" />
        )}
        <TextField label={t('auth.usernameOrEmailLabel')} value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} required
          fullWidth size="small" className="form-field" />
        <TextField type="password" label={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} required
          fullWidth size="small" className="form-field" />

        <button type="submit" disabled={status === 'loading'} className="login-btn">
          {status === 'loading' ? t('auth.loginLoading') : t('auth.login')}
        </button>

        <p className="text-center mt-lg text-sm ">
          <Link to="/forgot-password" className="text-muted detail-back-link">{t('auth.forgotPassword')}</Link>
        </p>
      </form>
      <div className="login-footer-links">
        <Link to="/privacy-policy" className="login-footer-links__link">
          {t('loginFooter.privacyPolicy')}
        </Link>
        <span className="login-footer-links__sep"> | </span>
        <Link to="/terms-of-service" className="login-footer-links__link">
          {t('loginFooter.termsOfService')}
        </Link>
      </div>
    </div>
  );
}
